import { OptionsItemLiveData, OptionsItemLocData, TextStatesData, TextureStatesData } from "../../../types/types";
import { ApplyMixins } from "../../../utils/ApplyMixins";
import { Interactive } from "../../../mixins/Interactive";
import { Showable } from "../../../mixins/Showable";
import { UpdateView } from "../../../mixins/UpdateView";
import { ISize } from "../../../controls/scrollpane/ISize";
import { RectSize } from "../../../utils/RectSize";
import { OptionsItemBase } from "./OptionsItemBase";
import { ChoiceSelector, ChoiceSelectorEvents } from "../../views/ChoiceSelector";
import { LoaderFileKeys } from "../../constant/AppConstants";
import isMobile from 'ismobilejs';

// get ts linting
export interface OptionsSliderItem extends Showable, Interactive, UpdateView { }


export class OptionsSliderItem
    extends OptionsItemBase
    implements ISize {
    /**
     * Index of this item in parent @see OptionsCategoryContent
     */
    // public index: number;
    // base:label => txtTitle
    // public txtValue: Phaser.GameObjects.Text;
    public slider: ChoiceSelector;

    /**
     * EDIT: we now use LIVE model @see itemLiveData for index and values both
     * All possible values this slider can display
     * Types can be (mixed): number | string
     */
    // protected values: any[];
    /**
     * current value index displayed in slider
     */
    // protected valueIndex: number;

    private static appliedMixins: boolean = false;

    protected onItemSliderChange: Function;
    protected onItemSliderClick: Function;


    public Init(
        // itemIndex: number,
        bgTextureStates: TextureStatesData,
        labelTextStates: TextStatesData,
        valueTextStates: TextStatesData,
        onItemSliderChange: Function,
        delimiterLineAlpha: number,
    ): void {
        if (!OptionsSliderItem.appliedMixins) {
            // ApplyMixins(OptionsSliderItem, [Showable, Interactive, UpdateView]);
            ApplyMixins(OptionsSliderItem, [Showable, UpdateView]);
            OptionsSliderItem.appliedMixins = true;
        }

        // this.index = itemIndex;
        // this.onItemSliderClick = onItemSliderClick;
        this.onItemSliderChange = onItemSliderChange;
        this.delimiterLineAlpha = delimiterLineAlpha;

        // use this when deciding exact item size @see parent->OnResize
        // this.type = "OptionsSliderItem";
        this.updateViews = [];
        this.size = new RectSize();
        // set default (mixin) props
        // NOTE: default values set during mixin declaration are undefined)
        // Showable
        this.isShowing = false;
        // Interactive
        // this.isInteractive = undefined;
        // UpdateView
        this.pointerOver = false;

        const _isMobile: boolean = isMobile(window.navigator).any;

        this.bg = this.scene.make.image({ key: bgTextureStates.out.texture, frame: bgTextureStates.out.frame });
        this.bg.setOrigin(0);
        this.add(this.bg);

        this.imgDelimiterLine = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "rect-2x2-white.png" });
        this.imgDelimiterLine.setOrigin(0, 0.5);
        this.imgDelimiterLine.alpha = this.delimiterLineAlpha;
        this.add(this.imgDelimiterLine);

        this.label = this.scene.make.text({
            // text: label, 
            style: labelTextStates.out,
        });
        this.label.setOrigin(0);
        this.add(this.label);

        if (!_isMobile) {
            this.AddViewDef({
                view: this.label,
                textStates: labelTextStates,
            });
        }

        // slider
        this.slider = new ChoiceSelector(this.scene);
        // this.slider.name = "OptionSliderItem_ChoiceSelector";
        this.slider.Init(
            {
                texture: LoaderFileKeys.ui, frame: "btn-oval9-bg-out.png"
            },
            _isMobile ?
                {
                    out: { texture: LoaderFileKeys.ui, frame: "btn-round2-bg-out.png" },
                    // over: { texture: LoaderFileKeys.ui, frame: "btn-round2-bg-over.png" },
                    disabled: { texture: LoaderFileKeys.ui, frame: "btn-round2-bg-disabled.png" }
                } :
                {
                    out: { texture: LoaderFileKeys.ui, frame: "btn-round2-bg-out.png" },
                    over: { texture: LoaderFileKeys.ui, frame: "btn-round2-bg-over.png" },
                    disabled: { texture: LoaderFileKeys.ui, frame: "btn-round2-bg-disabled.png" }
                },
            _isMobile ?
                {
                    out: { texture: LoaderFileKeys.ui, frame: "btn-round3-bg-out.png" },
                    // over: { texture: LoaderFileKeys.ui, frame: "btn-round3-bg-over.png" },
                    disabled: { texture: LoaderFileKeys.ui, frame: "btn-round2-bg-disabled.png" }
                } :
                {
                    out: { texture: LoaderFileKeys.ui, frame: "btn-round3-bg-out.png" },
                    over: { texture: LoaderFileKeys.ui, frame: "btn-round3-bg-over.png" },
                    disabled: { texture: LoaderFileKeys.ui, frame: "btn-round2-bg-disabled.png" }
                },
        );
        this.slider.Build();
        this.slider.bg.setAlpha(0.9);
        // this.slider.setScale(btnScale);
        this.add(this.slider);

        // EDIT: externally
        // this.RegisterListeners();
    }

    public override RegisterListeners(): void {
        // console.log("RegisterListeners");
        // super.RegisterListeners(); // trace all at once (prettier output)
        this.AddListeners();
        this.slider.SetInteractive(true);
        this.slider.GetEventEmitter().on(ChoiceSelectorEvents.CHANGE, this.OnSliderValueChange, this);
    }

    public override UnregisterListeners(): void {
        // console.log("UnregisterListeners");
        // super.UnregisterListeners(); // trace all at once (prettier output)
        this.RemoveListeners();
        this.slider.SetInteractive(false);
        this.slider.GetEventEmitter().off(ChoiceSelectorEvents.CHANGE, this.OnSliderValueChange);
    }

    protected OnSliderValueChange(newValue: number): void {
        // console.log(newValue);
        this.itemLiveData.value = newValue;

        // dispatch change
        this.onItemSliderChange?.(this, newValue);
    }

    /**
     * Data model(s) changed. Update view.
     * 
     * Implicit (@see SetData) or explicit (this) call to update from either:
     * 1) localization (texts changed) or 
     * 2) live data (value indexes changed) change
     * 
     * NOTE: The 'Data' in 'DataChange' means any data, eg live data, 
     * localization data or any other data this component uses.
     */
    public override UpdateViewFromDataChange(
        itemLocData: OptionsItemLocData = undefined,
        itemLiveData: OptionsItemLiveData = undefined
    ): void {
        this.imgDelimiterLine.visible = this.hasDelimiterLine;

        if (itemLocData != undefined) {
            // if (itemLiveData != undefined) {
            //     this.txtValue.text = itemLocData.choices[itemLiveData.value as number].itemLabel as string;
            // }
            this.label.text = itemLocData.itemLabel;

            this.slider.SetChoices(this.itemLocData.choices.map(choice =>
                choice.itemLabel as string
            ));
            // this.slider.SetStartChoiceIndex(0); // default: 0
            this.slider.SetChoiceIndex(this.itemLiveData.value as number);
            // console.log(this.itemLiveData.value as number);
            // this.slider.SetInteractive(true);
        }
    }

    /**
     * @alike: OnResize
     * Uses 'maximum allowed width' to compute item's resulting height
     * - Item's width and height depends on height of label textfield
     * - label's height depends on amount of HORIZONTAL space and maximum horizontal space allowed for item
     * 
     * @param width 
     */
    public override SetWidthGetHeight(width: number): number {
        // console.log("SetWidthGetHeight", width);

        const sidePaddingsSize = 2 * this.borderPadding.horizontal;

        this.slider.SetWidth(width - sidePaddingsSize);

        // resizes label textfield
        this.label.style.setWordWrapWidth(width - sidePaddingsSize);

        // const height = this.borderPadding.vertical + this.label.displayHeight + this.itemSpacing.height + this.txtValue.displayHeight + this.borderPadding.vertical;
        const height = this.borderPadding.vertical + this.label.displayHeight + this.itemSpacing.height + this.slider.bg.displayHeight + this.borderPadding.vertical;

        // computes and returns height
        return height;
    }

    public override OnResize(): void {
        this.bg.setDisplaySize(this.size.width, this.size.height);

        // delimiter (if item has it -- last item in list does not have it)
        if (this.hasDelimiterLine) {
            // origin 0,1 (bot left)
            this.imgDelimiterLine.y = this.size.height;
            this.imgDelimiterLine.displayWidth = this.size.width;
        }

        // label origin (0)
        this.label.setPosition(this.borderPadding.horizontal, this.borderPadding.vertical);
        // this.txtValue.setPosition(this.borderPadding.horizontal, this.borderPadding.vertical + this.label.displayHeight + this.itemSpacing.height);
        this.slider.setPosition(
            this.borderPadding.horizontal + this.slider.bg.displayWidth / 2,
            this.size.height - this.borderPadding.vertical - this.slider.bg.displayHeight / 2
        );

        // OptionsSlider item doesnt have hitArea
        // this.SetHitAreaRectangle?.(new Phaser.Geom.Rectangle(0, 0, this.size.width, this.size.height));
    }
}